import { api } from '@/lib/api/axios';
import { toApiError, toApiErrorFromBlob } from '@/lib/api/error';
import type { LogsheetDetailView } from '@/types/logsheet-view';

/**
 * `GET /api/logsheets/:id` — unified read-only detail.
 *
 * Backed by `LogsheetsController.GetById`. Site-scoped via `IUserScopeResolver`;
 * returns 404 when the entry doesn't exist OR is outside the actor's scope (no
 * existence leak). Action eligibility carried in `viewerActions` on the body.
 */
export async function getLogsheetDetailViewRequest(id: number): Promise<LogsheetDetailView> {
  try {
    const { data } = await api.get<LogsheetDetailView>(`/api/logsheets/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * `GET /api/logsheets/:id/documents/:docId` — download an attached document.
 *
 * Backed by `LogsheetsController.DownloadDocument`, which applies the SAME scope gate as
 * the detail GET above: if the entry is visible to the actor, so are its documents.
 *
 * Deliberately NOT the Maker route (`/api/logsheets/maker/...`) — that one is owner-gated,
 * which meant every Checker / Approver / Site Head viewing the shared detail page saw a
 * Download button that returned 403. Keep this call pointed at the unified surface.
 *
 * Errors go through `toApiErrorFromBlob` because `responseType: 'blob'` delivers the
 * problem+json error body as a Blob, which the plain parser cannot read.
 */
export async function downloadLogsheetDocumentRequest(entryId: number, docId: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `/api/logsheets/${String(entryId)}/documents/${String(docId)}`,
      { responseType: 'blob' },
    );
    return data;
  } catch (e) {
    throw await toApiErrorFromBlob(e);
  }
}
